import { useEffect, useRef, useState } from 'react'
import type { Functions } from 'firebase/functions'
import { api } from '../../../lib/api'

export interface ContactCompanyCandidate {
  uid: string
  name: string
  clientNumber: number | null
  archived?: boolean
}

type ContactResolutionReason =
  | 'employer'
  | 'employee'
  | 'contact_email'
  | 'ambiguous'
  | 'no_match'
  | 'invalid_email'

interface ResolveContactCompaniesResponse {
  reason?: ContactResolutionReason
  companyUid?: string
  candidates?: ContactCompanyCandidate[]
  result?: {
    reason?: ContactResolutionReason
    companyUid?: string
    candidates?: ContactCompanyCandidate[]
  }
}

export function useResolveContactCompanies(
  functions: Functions,
  scopedCompanyUid: string | null | undefined,
  contactId: string | undefined
) {
  const [resolvedCompanyUid, setResolvedCompanyUid] = useState<
    string | undefined
  >()
  const [selectedCompanyUid, setSelectedCompanyUid] = useState<
    string | undefined
  >()
  const [candidates, setCandidates] = useState<ContactCompanyCandidate[]>([])
  const [reason, setReason] = useState<ContactResolutionReason | undefined>()
  const [loading, setLoading] = useState(false)
  const [failed, setFailed] = useState(false)
  const [retryKey, setRetryKey] = useState(0)
  const requestIdRef = useRef(0)

  useEffect(() => {
    const requestId = ++requestIdRef.current

    if (scopedCompanyUid || !contactId) {
      setResolvedCompanyUid(undefined)
      setSelectedCompanyUid(undefined)
      setCandidates([])
      setReason(undefined)
      setLoading(false)
      setFailed(false)
      return
    }
    setResolvedCompanyUid(undefined)
    setSelectedCompanyUid(undefined)
    setCandidates([])
    setReason(undefined)
    setLoading(true)
    setFailed(false)

    const payload = contactId.startsWith('email:')
      ? { email: contactId.slice('email:'.length) }
      : { contactId: Number(contactId) }

    void (async () => {
      try {
        const response = (await api(
          functions,
          'chatwoot/resolve_contact_companies',
          payload
        )) as ResolveContactCompaniesResponse | undefined
        if (requestId !== requestIdRef.current) {
          return
        }
        const body = response?.result ?? response
        const nextReason = body?.reason
        setReason(nextReason)
        if (nextReason === 'ambiguous') {
          setCandidates(body?.candidates ?? [])
          return
        }
        setResolvedCompanyUid(body?.companyUid)
      } catch {
        if (requestId !== requestIdRef.current) {
          return
        }
        setFailed(true)
      } finally {
        if (requestId === requestIdRef.current) {
          setLoading(false)
        }
      }
    })()
  }, [contactId, functions, retryKey, scopedCompanyUid])

  const stampCompanyUid =
    scopedCompanyUid || selectedCompanyUid || resolvedCompanyUid

  const isScoped = Boolean(scopedCompanyUid)
  const hasContact = Boolean(contactId)
  const ambiguousPickComplete =
    reason !== 'ambiguous' || Boolean(selectedCompanyUid)
  const companyResolved =
    isScoped || (!loading && hasContact && (failed || ambiguousPickComplete))

  return {
    stampCompanyUid,
    resolvedCompanyUid,
    selectedCompanyUid,
    setSelectedCompanyUid,
    candidates,
    reason,
    loading,
    failed,
    retry: () => setRetryKey((key) => key + 1),
    needsCompanyPick: !scopedCompanyUid && reason === 'ambiguous',
    companyResolved,
  }
}
